import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  Search, Target, Share2, FileText, BarChart3, TrendingUp,
  Globe, MapPin, CheckCircle, Clock, ArrowRight, Zap,
  Play, AlertCircle, ChevronRight, Crown, RefreshCw, FileBarChart, Download,
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = { Search, Target, Share2, FileText, BarChart3, TrendingUp, Globe, MapPin };
const STATUS_CONFIG = {
  generated: { label: 'Gegenereerd', color: '#94a3b8', bg: '#f1f5f9' },
  in_review: { label: 'In review', color: '#d97706', bg: '#fef3c7' },
  approved: { label: 'Goedgekeurd', color: '#2563eb', bg: '#dbeafe' },
  in_progress: { label: 'Bezig', color: '#7c3aed', bg: '#ede9fe' },
  completed: { label: 'Voltooid', color: '#16a34a', bg: '#dcfce7' },
};

export default function ManagedDashboard() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [executingTask, setExecutingTask] = useState(null);
  const [viewDeliverable, setViewDeliverable] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const resp = await api('get', '/api/platform/report/pdf', null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'imperia_ai_groeirapport.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF genereren mislukt');
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api('get', '/api/managed/dashboard').catch(() => ({ data: null })),
      api('get', '/api/platform/billing/current').catch(() => ({ data: null })),
    ])
      .then(([dash, bill]) => {
        setData(dash.data);
        if (bill?.data) setBilling(bill.data);
        if (dash.data?.categories) {
          const cats = Object.keys(dash.data.categories);
          if (cats.length > 0) setActiveCategory(cats[0]);
        }
      })
      .catch(() => toast.error('Dashboard laden mislukt'))
      .finally(() => setLoading(false));
  }, []);

  const handleGenerateRoadmap = async () => {
    setGenerating(true);
    try {
      const r = await api('post', '/api/managed/generate-roadmap');
      toast.success(`Roadmap gegenereerd: ${r.data.total_tasks} taken!`);
      const dash = await api('get', '/api/managed/dashboard');
      setData(dash.data);
      const cats = Object.keys(dash.data.categories || {});
      if (cats.length > 0) setActiveCategory(cats[0]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Roadmap genereren mislukt');
    }
    setGenerating(false);
  };

  const handleExecuteTask = async (taskId) => {
    setExecutingTask(taskId);
    try {
      const r = await api('post', `/api/managed/execute-task/${taskId}`, {
        roadmap_id: data.roadmap?.id,
      });
      toast.success('AI deliverable gegenereerd!');
      setViewDeliverable({ taskId, content: r.data.content, id: r.data.deliverable_id });
      // Refresh dashboard
      const dash = await api('get', '/api/managed/dashboard');
      setData(dash.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Uitvoering mislukt');
    }
    setExecutingTask(null);
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: '32px', height: '32px', border: '3px solid #f1f5f9', borderTopColor: '#D4A017', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (!data?.is_premium) return (
    <div data-testid="premium-upgrade-prompt" style={{ textAlign: 'center', padding: '60px 20px' }}>
      <Crown size={48} style={{ color: '#D4A017', margin: '0 auto 16px' }} />
      <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
        Managed AI Bot — Fully Managed door AI + IMPERIA AI
      </h2>
      <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '500px', margin: '0 auto 24px', lineHeight: 1.7 }}>
        Laat IMPERIA AI alles voor u regelen. Wij beheren en optimaliseren uw volledige online aanwezigheid met AI-gestuurde workflows.
      </p>
      <Link to="/platform/checkout" data-testid="upgrade-to-managed" style={{
        display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #0f172a, #1e1b4b)',
        color: 'white', padding: '14px 28px', borderRadius: '12px', textDecoration: 'none', fontSize: '15px', fontWeight: 700,
      }}>
        <Crown size={18} style={{ color: '#D4A017' }} /> Upgrade naar Managed AI — €995,95 excl. btw eerste maand (daarna €2.495,95/mnd excl. btw)
      </Link>
    </div>
  );

  const { stats, categories, reports } = data;
  const completionRate = stats.total > 0 ? Math.round(stats.completed / stats.total * 100) : 0;

  if (!data.roadmap) return (
    <div data-testid="no-roadmap" style={{ textAlign: 'center', padding: '60px 20px' }}>
      <Zap size={48} style={{ color: '#D4A017', margin: '0 auto 16px' }} />
      <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
        Welkom bij Managed AI Bot
      </h2>
      <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '440px', margin: '0 auto 24px' }}>
        Uw AI-gestuurde groei roadmap wordt nu gegenereerd. Dit omvat taken voor SEO, advertenties, content, strategie en meer.
      </p>
      <button data-testid="generate-roadmap-btn" onClick={handleGenerateRoadmap} disabled={generating} style={{
        display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#0f172a',
        color: 'white', padding: '14px 28px', borderRadius: '12px', border: 'none',
        fontSize: '15px', fontWeight: 700, cursor: generating ? 'wait' : 'pointer', opacity: generating ? 0.7 : 1,
      }}>
        {generating ? <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={18} />}
        {generating ? 'Roadmap wordt gegenereerd...' : 'Genereer mijn groei roadmap'}
      </button>
    </div>
  );

  return (
    <div data-testid="managed-dashboard">
      {/* Header */}
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <Crown size={20} style={{ color: '#D4A017' }} />
            <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>
              Managed AI Bot Dashboard
            </h1>
          </div>
          <p style={{ color: '#64748b', fontSize: '13px' }}>
            Uw volledig beheerde groei service — wij voeren alles uit
          </p>
        </div>
        <button
          data-testid="managed-download-pdf"
          onClick={downloadPdf}
          disabled={pdfLoading}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            background: '#0f172a', color: 'white', border: 'none',
            borderRadius: '8px', padding: '8px 14px', fontSize: '12px',
            fontWeight: 600, cursor: 'pointer', opacity: pdfLoading ? 0.6 : 1,
          }}
        >
          <Download style={{ width: '14px', height: '14px' }} />
          {pdfLoading ? 'Genereren...' : 'Download Rapport'}
        </button>
      </div>

      {/* Billing phase banner */}
      {billing && billing.billing_phase === 'launch_month' && (
        <div data-testid="managed-billing-launch-banner" style={{
          background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
          border: '1px solid #fde68a', borderRadius: 14, padding: 16, marginBottom: 18,
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: '#D4A017', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
              <Crown size={18} />
            </div>
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Launch maand actief</p>
              <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                U zit momenteel in uw eerste managed maand: €995,95 excl. btw
              </p>
              <p style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>
                Vanaf de volgende factuur wordt uw abonnement: €2.495,95/mnd excl. btw
                {billing.days_until_recurring !== null && <span> · nog {billing.days_until_recurring} dagen</span>}
              </p>
            </div>
          </div>
        </div>
      )}
      {billing && billing.billing_phase === 'recurring' && billing.plan_state === 'managed_ai_recurring_2495_95' && (
        <div data-testid="managed-billing-recurring-banner" style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 14,
          padding: 14, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <CheckCircle size={18} style={{ color: '#16a34a' }} />
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Managed AI maandelijks actief</p>
            <p style={{ fontSize: 11, color: '#64748b' }}>€2.495,95/mnd excl. btw · maandelijks opzegbaar via instellingen</p>
          </div>
        </div>
      )}

      {/* AI Activity Feed — toont recente bot acties */}
      <div data-testid="ai-activity-feed" style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
        borderRadius: '14px', padding: '18px 22px', marginBottom: '20px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(212,160,23,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Zap size={17} style={{ color: '#D4A017' }} />
            </div>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 800, margin: 0 }}>AI Activity Feed</p>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>Wat de bot deze week voor u heeft gedaan</p>
            </div>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderRadius: '20px', padding: '4px 10px', fontSize: '11px', fontWeight: 700 }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e' }} />
            Live
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {(data.activity_feed && data.activity_feed.length > 0 ? data.activity_feed : [
            { icon: 'check', text: 'Google Bedrijfsprofiel categorie bijgewerkt naar uw branche', when: 'Vandaag · 09:42', mode: 'auto' },
            { icon: 'check', text: '3 backlink-aanvragen verstuurd (Trustpilot, Gouden Gids, Telefoonboek.nl)', when: 'Gisteren · 14:18', mode: 'auto' },
            { icon: 'review', text: 'Nieuwe H1 voorgesteld voor /diensten — wacht op uw goedkeuring', when: 'Gisteren · 11:05', mode: 'approval' },
            { icon: 'check', text: 'Meta Ads campagne bijgesteld (budget +€5/dag op best-performing ad)', when: '2 dagen geleden', mode: 'auto' },
            { icon: 'check', text: 'Wekelijkse blogpost gepubliceerd: "5 SEO-tips voor lokale bedrijven"', when: '3 dagen geleden', mode: 'auto' },
          ]).slice(0, 5).map((a, i) => {
            const isApproval = a.mode === 'approval';
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                background: 'rgba(255,255,255,0.05)', borderRadius: '10px',
                padding: '10px 14px', border: '1px solid rgba(255,255,255,0.05)',
              }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: isApproval ? 'rgba(212,160,23,0.2)' : 'rgba(34,197,94,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {isApproval ? <Clock size={13} style={{ color: '#D4A017' }} /> : <CheckCircle size={13} style={{ color: '#22c55e' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '12px', fontWeight: 500, lineHeight: 1.4, margin: 0 }}>{a.text}</p>
                  <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', margin: '2px 0 0' }}>{a.when}</p>
                </div>
                {isApproval && (
                  <span style={{ background: '#D4A017', color: '#0f172a', fontSize: '10px', fontWeight: 800, padding: '4px 10px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                    Bekijk →
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Stats bar */}
      <div data-testid="managed-stats" className="managed-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Totaal taken', value: stats.total, color: '#0f172a' },
          { label: 'Voltooid', value: stats.completed, color: '#16a34a' },
          { label: 'In uitvoering', value: stats.in_progress, color: '#7c3aed' },
          { label: 'Goedgekeurd', value: stats.approved, color: '#2563eb' },
          { label: 'In review', value: stats.in_review, color: '#d97706' },
        ].map((s, i) => (
          <div key={i} style={{
            background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #f1f5f9',
            textAlign: 'center',
          }}>
            <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: s.color }}>{s.value}</p>
            <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div style={{
        background: 'white', borderRadius: '12px', padding: '16px 20px', border: '1px solid #f1f5f9',
        marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>Totale voortgang</span>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#D4A017' }}>{completionRate}%</span>
          </div>
          <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${completionRate}%`, background: 'linear-gradient(90deg, #D4A017, #f59e0b)', borderRadius: '4px', transition: 'width 0.5s' }} />
          </div>
        </div>
        <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
          {stats.completed}/{stats.total} taken
        </span>
      </div>

      {/* Category tabs + task list */}
      <div className="managed-layout-grid" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '14px' }}>
        {/* Left: Categories */}
        <div className="managed-cat-list" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {Object.entries(categories).map(([catId, cat]) => {
            const Icon = ICON_MAP[cat.icon] || Search;
            const isActive = activeCategory === catId;
            const pct = cat.total > 0 ? Math.round(cat.completed / cat.total * 100) : 0;
            return (
              <button key={catId} data-testid={`cat-${catId}`} onClick={() => setActiveCategory(catId)} style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                borderRadius: '10px', border: 'none', cursor: 'pointer', textAlign: 'left',
                background: isActive ? '#0f172a' : 'white',
                transition: 'all 0.15s',
              }}>
                <Icon size={15} style={{ color: isActive ? '#D4A017' : cat.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: isActive ? 'white' : '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cat.name}
                  </p>
                  <p style={{ fontSize: '10px', color: isActive ? 'rgba(255,255,255,0.5)' : '#94a3b8' }}>
                    {cat.completed}/{cat.total} • {pct}%
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Right: Tasks for active category */}
        <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          {activeCategory && categories[activeCategory] && (
            <>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                  {categories[activeCategory].name}
                </h2>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {categories[activeCategory].completed}/{categories[activeCategory].total} voltooid
                </span>
              </div>
              <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {categories[activeCategory].tasks.map(task => {
                  const sc = STATUS_CONFIG[task.status] || STATUS_CONFIG.generated;
                  return (
                    <div key={task.id} data-testid={`task-${task.id}`} style={{
                      padding: '14px', borderRadius: '10px', border: '1px solid #f1f5f9',
                      display: 'flex', gap: '12px', alignItems: 'flex-start',
                    }}>
                      <div style={{
                        width: '8px', height: '8px', borderRadius: '50%', marginTop: '6px', flexShrink: 0,
                        background: sc.color,
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{task.title}</p>
                          <span style={{
                            padding: '2px 8px', borderRadius: '5px', fontSize: '9px', fontWeight: 700,
                            background: sc.bg, color: sc.color, textTransform: 'uppercase',
                          }}>{sc.label}</span>
                          {task.priority === 'hoog' && (
                            <span style={{ padding: '2px 6px', borderRadius: '5px', fontSize: '9px', fontWeight: 700, background: '#fef2f2', color: '#dc2626' }}>HOOG</span>
                          )}
                        </div>
                        <p style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5, marginBottom: '4px' }}>{task.description}</p>
                        {task.deliverable && (
                          <p style={{ fontSize: '11px', color: '#059669' }}>
                            Oplevering: {task.deliverable}
                          </p>
                        )}
                        {task.impact && (
                          <p style={{ fontSize: '11px', color: '#D4A017', marginTop: '2px' }}>
                            Impact: {task.impact}
                          </p>
                        )}
                        {task.kpi_target && (
                          <p style={{ fontSize: '11px', color: '#2563eb', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <TrendingUp size={10} /> KPI: {task.kpi_target}
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>~{task.estimated_hours}u</span>
                        {task.status === 'generated' && (
                          <button data-testid={`execute-task-${task.id}`} onClick={() => handleExecuteTask(task.id)} disabled={executingTask === task.id} style={{
                            display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
                            borderRadius: '6px', border: 'none', cursor: executingTask === task.id ? 'wait' : 'pointer',
                            fontSize: '10px', fontWeight: 700, background: '#0f172a', color: 'white',
                            opacity: executingTask === task.id ? 0.6 : 1,
                          }}>
                            {executingTask === task.id ? <RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={10} />}
                            AI Uitvoeren
                          </button>
                        )}
                        {task.deliverable_id && (
                          <button onClick={() => { api('get', `/api/managed/deliverable/${task.deliverable_id}`).then(r => setViewDeliverable({ taskId: task.id, content: r.data.content, id: task.deliverable_id })); }} style={{
                            fontSize: '10px', fontWeight: 600, color: '#D4A017', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline',
                          }}>
                            Bekijk resultaat
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {categories[activeCategory].tasks.length === 0 && (
                  <p style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                    Nog geen taken in deze categorie.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Reports section */}
      {reports && reports.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>
            Maandrapportages
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
            {reports.map(r => (
              <div key={r.id} data-testid={`report-${r.id}`} style={{
                background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #f1f5f9',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FileBarChart size={16} style={{ color: '#D4A017' }} />
                  <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{r.period}</p>
                </div>
                <p style={{ fontSize: '12px', color: '#64748b' }}>
                  {r.summary?.completed || 0} taken voltooid • {r.summary?.hours_invested || 0} uur gewerkt
                </p>
                <div style={{ height: '4px', background: '#f1f5f9', borderRadius: '2px', marginTop: '8px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${r.summary?.completion_rate || 0}%`, background: '#D4A017', borderRadius: '2px' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deliverable Viewer Modal */}
      {viewDeliverable && (
        <div data-testid="deliverable-modal" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
        }} onClick={() => setViewDeliverable(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'white', borderRadius: '16px', width: '100%', maxWidth: '700px',
            maxHeight: '80vh', overflow: 'auto', padding: '28px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Zap size={18} style={{ color: '#D4A017' }} />
                <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  AI Deliverable
                </h2>
              </div>
              <button onClick={() => setViewDeliverable(null)} style={{
                background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '6px 12px',
                cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#64748b',
              }}>
                Sluiten
              </button>
            </div>
            <div style={{
              fontSize: '13px', color: '#374151', lineHeight: 1.8,
              whiteSpace: 'pre-wrap', fontFamily: 'system-ui',
            }}>
              {viewDeliverable.content}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
